import type { Accessor } from "solid-js";
import { createMemo } from "solid-js";
import type { FreelancerSearchFilters } from "@/domains/company/active-search/types";
import { paramsToFilters } from "@/domains/company/active-search/utils/filterParams";

type SearchParams = Record<string, string | string[] | undefined>;

export type FreelancerFiltersWithPagination = FreelancerSearchFilters & {
  page?: number;
  per_page?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

function buildFiltersFromParams(
  searchParams: SearchParams,
): FreelancerFiltersWithPagination {
  const filters: FreelancerFiltersWithPagination = {
    ...paramsToFilters(searchParams),
  };

  const firstValue = (
    value: string | string[] | undefined,
  ): string | undefined =>
    typeof value === "string"
      ? value
      : Array.isArray(value)
        ? value[0]
        : undefined;

  const parseInteger = (
    value: string | string[] | undefined,
  ): number | undefined => {
    const first = firstValue(value);
    if (!first) return undefined;
    const parsed = Number.parseInt(first, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const page = parseInteger(searchParams.page);
  if (page !== undefined && page > 0) {
    filters.page = page;
  }

  // Accept the legacy `limit` URL key, but always shape the API request with
  // the backend's canonical `per_page` field.
  const perPage = parseInteger(searchParams.per_page ?? searchParams.limit);
  if (perPage !== undefined) {
    filters.per_page = Math.min(100, Math.max(1, perPage));
  }

  const sortBy = firstValue(searchParams.sortBy);
  if (sortBy) {
    filters.sortBy = sortBy;
  }

  const sortOrder = firstValue(searchParams.sortOrder);
  if (sortOrder === "asc" || sortOrder === "desc") {
    filters.sortOrder = sortOrder;
  }

  return filters;
}

/** Returns a reactive accessor that recomputes when searchParams (URL) changes. */
export const useFreelancerFiltersFromSearchParams = (
  searchParams: SearchParams,
): Accessor<FreelancerFiltersWithPagination> => {
  return createMemo(() => buildFiltersFromParams(searchParams));
};
